import { IconButton } from "@/subframe/components/IconButton";
import { Button } from "@/subframe/components/Button";
import { DropdownMenu } from "@/subframe/components/DropdownMenu";
import * as SubframeCore from "@subframe/core";
import { Switch } from "@/subframe/components/Switch";
import { TextArea } from "@/subframe/components/TextArea";
import { TextFieldUnstyled } from "@/subframe/components/TextFieldUnstyled";
import { Recipient } from "../redux/interfaces/reducersInterfaces";
import ReactCountryFlag from "react-country-flag";
import {getCountryCodeFromNumber } from "../Utils/countryNameToCode";
import { MessagesDataWithPagination } from "../Interfaces/messagesInterface";
import { getLatestReadMessage } from "../Utils/messagesHelper";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Alert } from "@/subframe/components/Alert";
import { useDispatch, useSelector } from "react-redux";
import { updateRecipient } from "../redux/reducers/messengerSlice";
import { AppDispatch, RootState } from "../redux/store";
import { formatPhoneNumber } from "../Utils/formatPhoneNumber";
import axios from "axios";

import { addLabel, removeLabel } from "../redux/reducers/authSlice";
import { Loader } from "@/subframe/components/Loader";
import { addNotification } from "../redux/reducers/notificationSlice";
import { NotificationIcon, NotificationType } from "../Utils/notificationEnums";

interface FriendInfoModalProps {
  currentRecipient: Recipient | null;
  messages: MessagesDataWithPagination;
  isSwitchOn: boolean;
  onToggle: () => void;
  isOpen: boolean;
  onClose: () => void;
}

function FriendInfoModal({
  currentRecipient,
  messages,
  isSwitchOn,
  onToggle,
  isOpen,
  onClose,
}: FriendInfoModalProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [lastReadStatus, setLastReadStatus] = useState<string | null>(null);
  const [name, setName] = useState(
    currentRecipient?.name || "No name available"
  );
  const nameRef = useRef<HTMLInputElement | null>(null);

  const [globalLabels, setGlobalLabels] = useState<string[]>(
    currentRecipient?.global_label || []
  );
  const { labels, token } = useSelector((state: RootState) => state.auth);
  const [label, setLabel] = useState("");
  const [loadingForLabelCreation, setLoadingForLabelCreation] = useState(false);
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [isNoteEditing, setIsNoteEditing] = useState(false);
  const [note, setNote] = useState(currentRecipient?.note || "");
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const countryCode =
    currentRecipient && getCountryCodeFromNumber(currentRecipient?.phone_number.toString());

  // Get the time difference with Moment.js
  const getTimeDifference = (readTime: number | null) => {
    const currentTime = moment();
    const readMoment = moment(readTime);
    return readMoment.from(currentTime);
  };

  const latestReadMessage =
    messages && messages.items && getLatestReadMessage(messages.items);

  useEffect(() => {
    if (latestReadMessage) {
      const timeDifference = getTimeDifference(latestReadMessage.read_time);
      setLastReadStatus(timeDifference);
    } else {
      setLastReadStatus("No messages have been read.");
    }
  }, [latestReadMessage]);

  //Update Name
  const handleSaveName = () => {
    setIsNameEditing(false);
    if (
      currentRecipient &&
      currentRecipient.id &&
      name !== currentRecipient.name
    ) {
      const { id, manual_label, ...rest } = currentRecipient;
      dispatch(
        updateRecipient({
          id,
          ...rest,
          manual_label: Array.isArray(manual_label) ? manual_label : [],
          name,
        })
      );
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.SUCCESS,
          title: "Name updated successfully",
          message: "",
          icon: NotificationIcon.CHECK,
        })
      );
      nameRef.current?.blur();
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSaveName();
    }
  };

  //Update Note
  const handleNoteKeyDown = async (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      await saveNote();
      textAreaRef.current?.blur();
    }
  };

  const handleNoteBlur = async () => {
    if (isNoteEditing) {
      await saveNote();
    }
  };

  const saveNote = async () => {
    setIsNoteEditing(false);

    if (
      currentRecipient &&
      currentRecipient.id &&
      note !== currentRecipient?.note
    ) {
      const { id, manual_label, ...rest } = currentRecipient;
      dispatch(
        updateRecipient({
          id,
          ...rest,
          manual_label: Array.isArray(manual_label) ? manual_label : [],
          note,
        })
      );
    }
  };

  useEffect(() => {
    setNote(currentRecipient?.note || "");
  }, [currentRecipient?.note]);

  useEffect(() => {
    setName(currentRecipient?.name || "");
  }, [currentRecipient?.name]);

  const handleRemoveGlobalLabel = (labelToRemove: string) => {
    const updatedLabels = globalLabels?.filter(
      (label) => label !== labelToRemove
    );

    setGlobalLabels(updatedLabels);

    if (currentRecipient && currentRecipient.id) {
      const { id, ...rest } = currentRecipient;
      dispatch(
        updateRecipient({
          id,
          ...rest,
          global_label: updatedLabels,
        })
      );
    }
  };

  useEffect(() => {
    setGlobalLabels(currentRecipient?.global_label || []);
  }, [currentRecipient?.global_label]);

  const handleAssignLabel = (label: string) => {
    if (currentRecipient && currentRecipient.id) {
      if (globalLabels.includes(label)) {
        return;
      }

      const updatedLabels = [...(globalLabels || []), label];

      const { id, ...rest } = currentRecipient;
      dispatch(
        updateRecipient({
          id,
          ...rest,
          global_label: updatedLabels,
        })
      );
      setGlobalLabels(updatedLabels);
    }
  };

  const handleCreateLabel = async () => {
    if (!label.trim()) {
      return;
    }
    setLoadingForLabelCreation(true);

    try {
      const response = await axios.post(
        "https://api.insightssystem.com/api:uYdlVwac/web/label_management",
        {
          label,
          keywords: [],
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: token })
          }
        }
      );

      dispatch(addLabel(response.data));
      setLoadingForLabelCreation(true);
      setLabel("");
    } catch (err) {
      console.log(err);
      
    } finally {
      setLoadingForLabelCreation(false);
    }
  };

  const handleDeleteLabel = async (labelId: number) => {
    if (!labelId) return;

    try {
      await axios.delete(
        `https://api.insightssystem.com/api:uYdlVwac/web/label_management/${labelId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: token })
          }
        }
      );
      dispatch(removeLabel(labelId));
    } catch (err) {
      console.log(err);
    }
  };

  // Don't render if modal is not open
  if (!isOpen) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-all duration-300"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-default-background rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col border border-solid border-neutral-border">
          {/* Modal Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-solid border-neutral-border bg-neutral-100">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-brand-600/20 border border-solid border-brand-600/35">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 text-brand-primary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-base font-semibold text-default-font">
                  Contact Information
                </h2>
                <p className="text-xs text-subtext-color">View and edit contact details</p>
              </div>
            </div>
            <IconButton
              variant="brand-tertiary"
              size="medium"
              icon="FeatherX"
              onClick={onClose}
              className="text-subtext-color hover:text-default-font hover:bg-neutral-200 rounded-full transition-all duration-200"
            />
          </div>

          {/* Modal Body */}
          <div className="flex-1 overflow-y-auto p-5 bg-default-background">
            <div className="flex w-full flex-col items-start gap-4">
              {/* Profile Section */}
              <div className="flex items-start gap-4 w-full">
                <div className="flex flex-col flex-wrap items-center justify-center gap-2 relative">
                  <div className="relative">
                    <svg
                      className="h-14 w-14 flex-none rounded-full shadow-lg"
                      xmlns="http://www.w3.org/2000/svg"
                      width="401px"
                      height="401px"
                      enable-background="new 312.809 0 401 401"
                      version="1.1"
                      viewBox="312.809 0 401 401"
                    >
                      <g transform="matrix(1.223 0 0 1.223 -467.5 -843.44)">
                        <rect
                          x="601.45"
                          y="653.07"
                          width="401"
                          height="401"
                          fill="#E4E6E7"
                        />
                        <path
                          d="m802.38 908.08c-84.515 0-153.52 48.185-157.38 108.62h314.79c-3.87-60.44-72.9-108.62-157.41-108.62z"
                          fill="#AEB4B7"
                        />
                        <path
                          d="m881.37 818.86c0 46.746-35.106 84.641-78.41 84.641s-78.41-37.895-78.41-84.641 35.106-84.641 78.41-84.641c43.31 0 78.41 37.9 78.41 84.64z"
                          fill="#AEB4B7"
                        />
                      </g>
                    </svg>
                    <div className="absolute -bottom-1 -right-1">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full border-2 border-solid border-neutral-border bg-default-background shadow-md">
                        <ReactCountryFlag
                          style={{
                            width: "1.2em",
                            height: "1.2em",
                          }}
                          countryCode={countryCode?.countryCode ? countryCode.countryCode : "US"}
                          svg
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <span className="text-xs font-medium text-default-font bg-neutral-100 px-2 py-1 rounded-full border border-solid border-neutral-border">
                      {currentRecipient && currentRecipient.country
                        ? currentRecipient.country
                        : "No country available"}
                    </span>
                  </div>
                </div>
                <div className="flex-1 flex flex-col items-start gap-2">
                  <div
                    className="w-full"
                    title={!isNameEditing ? "Tap to edit name" : ""}
                  >
                    <TextFieldUnstyled>
                      <TextFieldUnstyled.Input
                        ref={nameRef}
                        placeholder="No name available"
                        value={name}
                        onFocus={() => setIsNameEditing(true)}
                        onBlur={handleSaveName}
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={handleNameKeyDown}
                        className={`text-base font-semibold text-default-font w-full rounded-md transition-all duration-200 ${
                          isNameEditing
                            ? "border-2 border-solid border-brand-600 bg-neutral-100 px-2.5 py-1.5"
                            : "border-2 border-transparent bg-transparent px-0 py-0"
                        }`}
                        style={{
                          cursor: isNameEditing ? "text" : "pointer",
                          outline: "none",
                          width: "100%",
                        }}
                        onClick={() => {
                          if (!isNameEditing) {
                            setIsNameEditing(true);
                          }
                        }}
                      />
                    </TextFieldUnstyled>
                  </div>
                  <div className="flex items-center gap-2 text-subtext-color">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3 text-subtext-color shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.554.894l3.4 8a1 1 0 01-.554 1.894H9M3 5a2 2 0 002 2h3.28a1 1 0 01.554.894l3.4 8a1 1 0 01-.554 1.894H9M3 5a2 2 0 002 2h3.28a1 1 0 01.554.894l3.4 8a1 1 0 01-.554 1.894H9"
                      />
                    </svg>
                    <span className="text-xs font-medium text-default-font">
                      {countryCode?.countryCode && currentRecipient?.phone_number
                        ? formatPhoneNumber(
                            currentRecipient.phone_number,
                            countryCode.countryCode
                          )
                        : currentRecipient?.phone_number
                        ? currentRecipient.phone_number
                        : "No number available"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Last Read Section */}
              <div className="w-full p-3 rounded-lg border border-solid border-neutral-border bg-neutral-100">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-brand-600/20 border border-solid border-brand-600/35">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-2.5 w-2.5 text-brand-primary"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <span className="text-xs font-semibold text-default-font">
                    Last Read
                  </span>
                </div>
                <span className="text-xs text-subtext-color ml-7">
                  {lastReadStatus}
                </span>
              </div>

              {/* Labels Section */}
              <div className="w-full">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-green-100">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-2.5 w-2.5 text-green-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                      />
                    </svg>
                  </div>
                  <span className="text-xs font-semibold text-default-font">
                    Labels
                  </span>
                  <IconButton
                    size="small"
                    icon="FeatherSettings"
                    className="text-subtext-color hover:text-default-font"
                  />
                </div>
                <div className="flex w-full flex-wrap items-center gap-2 mb-3">
                  {globalLabels?.map((label, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-1.5 rounded-full border border-solid border-neutral-border bg-neutral-100 px-2.5 py-1 shadow-sm hover:shadow-md transition-all duration-200"
                    >
                      <span className="text-xs font-medium text-default-font">
                        {label}
                      </span>
                      <IconButton
                        size="small"
                        icon="FeatherX"
                        onClick={() => handleRemoveGlobalLabel(label)}
                        className="text-subtext-color hover:text-red-500 hover:bg-red-500/15 transition-all duration-200"
                      />
                    </div>
                  ))}
                  <SubframeCore.DropdownMenu.Root>
                    <SubframeCore.DropdownMenu.Trigger asChild={true}>
                      <Button
                        className="h-8 px-3 rounded-full border-2 border-dashed border-neutral-300 hover:border-neutral-400 hover:bg-neutral-100 transition-all duration-200"
                        variant="neutral-secondary"
                        iconRight="FeatherChevronDown"
                      >
                        Create or Assign Label
                      </Button>
                    </SubframeCore.DropdownMenu.Trigger>
                    <SubframeCore.DropdownMenu.Portal>
                      <SubframeCore.DropdownMenu.Content
                        side="bottom"
                        align="start"
                        sideOffset={2}
                        asChild={true}
                        className="z-[60]"
                      >
                        <DropdownMenu>
                          <div className="flex items-center gap-2 p-2">
                            <TextFieldUnstyled>
                              <TextFieldUnstyled.Input
                                className="h-8 w-full flex-none rounded-lg border border-solid border-neutral-border bg-default-background px-3 text-xs text-default-font placeholder:text-subtext-color"
                                placeholder="Create new label..."
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                disabled={loadingForLabelCreation}
                              />
                            </TextFieldUnstyled>
                            {loadingForLabelCreation ? (
                              <Loader size="small" />
                            ) : (
                              <IconButton
                                variant="inverse"
                                size="small"
                                icon="FeatherCheck"
                                onClick={handleCreateLabel}
                                disabled={loadingForLabelCreation}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                              />
                            )}
                          </div>
                          <div className="h-px w-full bg-neutral-300" />
                          <div className="max-h-40 overflow-y-auto">
                            {labels?.map((labelItem) => {
                              const isAssigned = globalLabels.includes(
                                labelItem.label
                              );

                              return (
                                <div
                                  key={labelItem.id}
                                  className="flex w-full items-center gap-2 p-2 hover:bg-neutral-100"
                                >
                                  <DropdownMenu.DropdownItem
                                    icon={null}
                                    className={`w-full ${
                                      isAssigned
                                        ? "pointer-events-none bg-neutral-200 text-subtext-color"
                                        : "hover:bg-neutral-100"
                                    }`}
                                    onClick={() =>
                                      handleAssignLabel(labelItem.label)
                                    }
                                  >
                                    <div className="flex items-center justify-between w-full">
                                      <span className="text-xs text-default-font">
                                        {labelItem.label}
                                      </span>
                                      {isAssigned && (
                                        <span className="text-xs text-subtext-color ml-2">
                                          ✓ Assigned
                                        </span>
                                      )}
                                    </div>
                                  </DropdownMenu.DropdownItem>
                                  <IconButton
                                    size="small"
                                    icon="FeatherX"
                                    className="text-subtext-color hover:text-red-500 hover:bg-red-500/15"
                                    onClick={() => handleDeleteLabel(labelItem.id)}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </DropdownMenu>
                      </SubframeCore.DropdownMenu.Content>
                    </SubframeCore.DropdownMenu.Portal>
                  </SubframeCore.DropdownMenu.Root>
                </div>
              </div>

              {/* Chat Handoff Section */}
              <div className="w-full p-4 bg-neutral-100 rounded-lg border border-solid border-neutral-border">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-neutral-200">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-2.5 w-2.5 text-subtext-color"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </div>
                  <span className="text-xs font-semibold text-default-font">
                    Chat Handoff
                  </span>
                </div>
                <div className="flex w-full items-center justify-center gap-3">
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="w-6 h-6 rounded-full bg-default-background border-2 border-solid border-neutral-border flex items-center justify-center shadow-sm">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3 text-subtext-color"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <span className="text-xs font-medium text-subtext-color">Human Agent</span>
                  </div>
                  <Switch checked={isSwitchOn} onCheckedChange={onToggle} />
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="w-6 h-6 rounded-full bg-default-background border-2 border-solid border-neutral-border flex items-center justify-center shadow-sm">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3 text-subtext-color"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <span className="text-xs font-medium text-subtext-color">AI Assistant</span>
                  </div>
                </div>
              </div>

              {/* AI Assistant Alert */}
              <Alert
                title="AI Assistant"
                description={
                  "Enabled: Messages will be shared with your connected automation tools (Zapier, Make, etc.)\nDisabled: Messages won't sync with automation tools"
                }
                className="border border-solid border-brand-600/35 bg-neutral-100"
              />

              {/* Notes Section */}
              <div className="w-full">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-purple-100">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-2.5 w-2.5 text-purple-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </div>
                  <span className="text-xs font-semibold text-default-font">
                    Notes
                  </span>
                </div>
                <TextArea
                  className="w-full"
                  variant="filled"
                  label=""
                  helpText=""
                >
                  <TextArea.Input
                    ref={textAreaRef}
                    className="min-h-[80px] rounded-lg border-solid border-neutral-border focus:border-brand-500 focus:ring-brand-600/25 transition-all duration-200"
                    placeholder="Add notes about this contact..."
                    value={note}
                    onFocus={() => setIsNoteEditing(true)}
                    onChange={(e) => setNote(e.target.value)}
                    onBlur={handleNoteBlur}
                    onKeyDown={handleNoteKeyDown}
                  />
                </TextArea>
              </div>

              {/* Automation Note */}
              {currentRecipient?.note_auto && (
                <div className="w-full">
                  <div className="w-full rounded-lg border border-solid border-amber-500/40 bg-amber-500/10 p-3 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/20 border border-solid border-amber-500/35">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-2.5 w-2.5 text-amber-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                      </div>
                      <span className="text-xs font-semibold text-default-font">Automation Note</span>
                    </div>
                    <p className="text-xs text-subtext-color whitespace-pre-wrap ml-7">
                      {currentRecipient.note_auto}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default FriendInfoModal;
